import React, { useState } from 'react';
import { Modal, Button, Typography, useToast, Loading, Alert, AlertDescription } from '@crisellevaldez/speculo-ui';
import { Info, AlertTriangle } from 'lucide-react';
import { Organization } from '../../../types/organization.types';
import { useTenant } from '../../../hooks/client/tenant/useTenant';
import { CreateWebsiteForm } from './forms/CreateWebsiteForm';
import { UpdateWebsiteForm } from './forms/UpdateWebsiteForm';
import { UnifiedBrandingModal } from './branding/UnifiedBrandingModal';
import { MLSManagementModal } from './mls/MLSManagementModal';
import { useUser } from '../../../hooks/useUser';
import { AdminLockOverlay } from './AdminLockOverlay';
import { resolveWebsitesModalView } from './websitesModalView';

interface WebsitesModalProps {
  isOpen: boolean;
  onClose: () => void;
  selectedOrg: Organization | undefined;
  onSuccess?: () => void;
  customNoWebsiteMessage?: string;
  showCreateForm?: boolean; // Controls whether to show the create form or just a message
}

export const WebsitesModal: React.FC<WebsitesModalProps> = ({
  isOpen,
  onClose,
  selectedOrg,
  onSuccess,
  customNoWebsiteMessage,
  showCreateForm = true, // Default to showing the create form for backward compatibility
}) => {
  const { showToast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isCitiesLoading, setIsCitiesLoading] = useState(false);
  const [isUnifiedBrandingModalOpen, setIsUnifiedBrandingModalOpen] = useState(false);
  const [isMLSManagementModalOpen, setIsMLSManagementModalOpen] = useState(false);
  // Query to get tenant data
  const { data: tenant, isLoading, isError, refetch } = useTenant(selectedOrg?.id, isOpen);
  // The storefront returns 200 + defaults even for orgs with no IDX, so `tenant`
  // is always truthy — key create-vs-update off the explicit `configured` flag,
  // and treat a failed read (isError) as unknown, not "no site".
  const view = resolveWebsitesModalView({
    isLoading,
    isError,
    configured: tenant?.configured,
    showCreateForm,
  });
  const hasWebsite = view === 'update';
  // Get user role to determine permissions
  const { isAdmin, isSuperAdmin } = useUser();

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="2xl">
      <Modal.Header>
        <div className="flex items-center justify-between w-full">
          <Typography variant="h3">
            {isLoading
              ? ''
              : view === 'error'
                ? 'IDX Site Settings'
                : hasWebsite
                  ? 'Update IDX Site Settings'
                  : 'Create IDX Site'}
          </Typography>
        </div>
      </Modal.Header>
      <Modal.Body className="py-8">
        {isLoading ? (
          <div className="flex justify-center py-8">
            <Loading />
          </div>
        ) : view === 'error' ? (
          <div className="space-y-6">
            <Alert variant="destructive">
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription>
                We couldn&apos;t load this organization&apos;s IDX configuration.
                This is usually temporary — please try again.
              </AlertDescription>
            </Alert>
            <div className="flex justify-center">
              <Button variant="outline" onClick={() => refetch()}>
                Retry
              </Button>
            </div>
          </div>
        ) : (
          <div className="space-y-6">
            {/* Info alert for admins; non-admins get the lock banner from
                AdminLockOverlay instead. Create flow keeps its own warning. */}
            {((hasWebsite && isAdmin) || (!hasWebsite && showCreateForm)) && (
              <Alert variant={hasWebsite ? 'info' : 'warning'}>
                {hasWebsite ? <Info className="h-4 w-4" /> : <AlertTriangle className="h-4 w-4" />}
                <AlertDescription>
                  {hasWebsite
                    ? isSuperAdmin
                      ? 'Update your website settings and analytics configuration.'
                      : 'Update your website settings and branding configuration.'
                    : 'Set up a new website for your organization. Building your website may take up to 24 hours or more.'}
                </AlertDescription>
              </Alert>
            )}

            {tenant && hasWebsite ? (
              <>
                <AdminLockOverlay locked={!isAdmin} subject="IDX site settings" disableFields={false}>
                <UpdateWebsiteForm
                  tenant={tenant}
                  organizationId={selectedOrg?.id || ''}
                  locked={!isAdmin}
                  onSubmitting={(submitting) => {
                    setIsSubmitting(submitting);
                  }}
                  onCitiesLoading={(loading) => {
                    setIsCitiesLoading(loading);
                  }}
                  onSuccess={() => {
                    showToast({
                      message: 'Website settings updated successfully',
                      variant: 'success',
                    });
                    onSuccess?.();
                  }}
                  onError={(error) => {
                    showToast({
                      message: error.message,
                      variant: 'error',
                    });
                  }}
                  onBrandingClick={() => setIsUnifiedBrandingModalOpen(true)}
                  onMLSClick={() => setIsMLSManagementModalOpen(true)}
                  isSuperAdmin={isSuperAdmin}
                />
                </AdminLockOverlay>
                <UnifiedBrandingModal
                  isOpen={isUnifiedBrandingModalOpen}
                  onClose={() => setIsUnifiedBrandingModalOpen(false)}
                  selectedOrg={selectedOrg}
                  onSuccess={onSuccess}
                />
                <MLSManagementModal
                  isOpen={isMLSManagementModalOpen}
                  onClose={() => setIsMLSManagementModalOpen(false)}
                  selectedOrg={selectedOrg}
                  onSuccess={() => {
                    setIsMLSManagementModalOpen(false);
                    onSuccess?.();
                  }}
                  isSuperAdmin={isSuperAdmin}
                />
              </>
            ) : showCreateForm ? (
              <CreateWebsiteForm
                organizationId={selectedOrg?.id || ''}
                onSubmitting={(submitting) => {
                  setIsSubmitting(submitting);
                }}
                onSuccess={() => {
                  showToast({
                    message: 'Website created successfully',
                    variant: 'success',
                  });
                  onSuccess?.();
                  onClose();
                }}
                onError={(error) => {
                  showToast({
                    message: error.message,
                    variant: 'error',
                  });
                }}
              />
            ) : !showCreateForm ? (
              <div className="bg-white dark:bg-white/5 p-6 rounded-lg border border-gray-200 dark:border-white/10 flex items-center justify-center">
                <div className="text-center py-8">
                  <h3 className="text-lg font-medium text-foreground mb-2">No Website Available</h3>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">
                    {customNoWebsiteMessage ||
                      "To create a website, please reach out to our support team, and they'll be happy to help you get set up!"}
                  </p>
                </div>
              </div>
            ) : null}
          </div>
        )}
      </Modal.Body>
      {!isLoading && (
        <Modal.Footer>
          <div className="flex flex-col md:flex-row items-start md:items-center gap-4 w-full">
            <div className="flex gap-2 w-full md:w-auto ml-auto justify-end">
              <Button variant="outline" onClick={onClose}>
                Cancel
              </Button>
              {/* Submit only when we know the site state — never on a failed read */}
              {view !== 'error' && (hasWebsite || showCreateForm) && (
                <Button
                  variant="primary"
                  type="submit"
                  form={hasWebsite ? 'update-website-form' : 'create-website-form'}
                  isLoading={isSubmitting || isCitiesLoading}
                  disabled={hasWebsite && !isAdmin}
                >
                  {hasWebsite ? 'Update Settings' : 'Create Website'}
                </Button>
              )}
            </div>
          </div>
        </Modal.Footer>
      )}
    </Modal>
  );
};
